<script setup lang="ts">
import type { EventContactDTO } from '~/shared/api/client/events/events.dto';

const props = defineProps<{
  contacts: EventContactDTO[];
}>();

const groupedContacts = computed(() => {
  const groups = new Map<string, EventContactDTO[]>();

  for (const c of props.contacts) {
    const role = c.employee_role.role;
    if (!groups.has(role)) {
      groups.set(role, []);
    }
    groups.get(role)!.push(c);
  }

  return [...groups.entries()];
});
</script>

<template>
  <div v-if="contacts.length" :class="$style.contacts">
    <h2 :class="$style.title">Контактная информация</h2>

    <div
      v-for="[role, contactList] in groupedContacts"
      :key="role"
      :class="$style.group"
    >
      <div :class="$style.roleHeader">{{ role }}</div>

      <div
        v-for="contact in contactList"
        :key="contact.id"
        :class="$style.contactRow"
      >
        <div :class="$style.person">
          <span :class="$style.personName">{{ contact.employee.name }}</span>
          <span :class="$style.personPosition">{{
            contact.employee.position
          }}</span>
        </div>
        <div :class="$style.contactInfo">
          <div v-if="contact.employee.phone" :class="$style.infoBlock">
            <span :class="$style.infoLabel">Телефон</span>
            <div :class="$style.infoValue">
              <Icon name="my-icon:i-call" :size="20" />
              <a
                :href="`tel:${contact.employee.phone}`"
                :class="$style.infoLink"
              >
                {{ contact.employee.phone }}
              </a>
            </div>
          </div>
          <div v-if="contact.employee.email" :class="$style.infoBlock">
            <span :class="$style.infoLabel">Email</span>
            <div :class="$style.infoValue">
              <Icon name="my-icon:i-mail" :size="20" />
              <a
                :href="`mailto:${contact.employee.email}`"
                :class="$style.infoLink"
              >
                {{ contact.employee.email }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0 0 24px;
}

.group {
  border: 1px solid var(--color-gray-line);

  & + & {
    border-top: none;
  }
}

.roleHeader {
  @include text_caption1_caps;
  color: var(--color-gray-text);
  padding: 14px 20px;
  border-bottom: 1px solid var(--color-gray-line);
}

.contactRow {
  display: flex;
  border-bottom: 1px solid var(--color-gray-line);

  &:last-child {
    border-bottom: none;
  }
}

.person {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-0-5x);
  padding: 12px 20px;
}

.personName {
  @include text_body1_medium;
  color: var(--color-black-1);
}

.personPosition {
  @include text_body2;
  color: var(--color-gray-text);
}

.contactInfo {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-0-5x);
  padding: 12px 0;
  border-left: 1px solid var(--color-gray-line);
}

.infoBlock {
  display: flex;
  align-items: center;
  gap: var(--space-2x);
  padding: 0 20px;
}

.infoLabel {
  @include text_caption1_caps;
  color: var(--color-gray-text);
  width: 80px;
  flex-shrink: 0;
}

.infoValue {
  display: flex;
  align-items: center;
  gap: var(--space-1x);
  color: var(--color-red-1);

  svg path {
    fill: var(--color-red-1);
  }
}

.infoLink {
  @include text_caption1_caps;
  color: var(--color-red-1);
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
}

@media (max-width: 1379px) {
  .contacts {
    padding-left: 20px;
    padding-right: 20px;
  }

  .title {
    font-size: 32px;
  }
}

@media (max-width: 1023px) {
  .title {
    font-size: 24px;
  }
}

@media (max-width: 599px) {
  .personName {
    font-size: 14px;
  }

  .roleHeader,
  .personPosition,
  .infoLabel,
  .infoLink {
    font-size: 10px;
  }
}

@media (max-width: 479px) {
  .title {
    font-size: 16px;
  }

  .contactRow {
    flex-direction: column;
  }

  .person {
    border-bottom: 1px solid var(--color-gray-line);
  }

  .contactInfo {
    border-left: none;
  }
}
</style>
